
import React, { useState, useEffect } from 'react';
import { AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  isBluetoothSupported,
  bluetoothScan,
  connectToDevice,
} from '@/utils/bluetoothService';
import { usePayment } from '@/context/payment';
import { BluetoothDevice } from '@/context/payment/PaymentContextTypes';
// Import new subcomponents
import BluetoothScanButton from './bluetooth/BluetoothScanButton';
import BluetoothDeviceList from './bluetooth/BluetoothDeviceList';
import BluetoothNoDevices from './bluetooth/BluetoothNoDevices';
import { Button } from '@/components/ui/button';

const BluetoothScanner = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [devices, setDevices] = useState<BluetoothDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<BluetoothDevice | null>(null);
  const [isBluetoothAvailable, setIsBluetoothAvailable] = useState(true);
  const [hasPermissions, setHasPermissions] = useState(false);
  const { setConnectedDevice, connectedDevice } = usePayment();
  const navigate = useNavigate();

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    try {
      if ('permissions' in navigator) {
        const result = await navigator.permissions.query({ name: 'bluetooth' as PermissionName });
        setHasPermissions(result.state === 'granted');

        result.addEventListener('change', () => {
          setHasPermissions(result.state === 'granted');
        });
      }
    } catch (error) {
      console.log('Permissions API not supported or bluetooth permission not available');
    }
  };

  useEffect(() => {
    const checkBluetoothSupport = () => {
      const supported = isBluetoothSupported();
      setIsBluetoothAvailable(supported);

      if (!supported) {
        console.log('Bluetooth is not supported on this device or browser');
        toast.warning('Bluetooth is not supported on this device or browser.');
      }
    };

    checkBluetoothSupport();
    if (connectedDevice) {
      setSelectedDevice(connectedDevice);
    }
  }, [connectedDevice]);

  const requestPermissions = async () => {
    try {
      if ((navigator as any).bluetooth) {
        await (navigator as any).bluetooth.requestDevice({
          acceptAllDevices: true,
        });
        setHasPermissions(true);
        toast.success('Bluetooth permissions granted');
      } else {
        toast.error('Bluetooth is not supported on this device or browser');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'SecurityError') {
        toast.error('Bluetooth access was blocked. Please enable Bluetooth permissions in your browser settings.');
      } else if (error instanceof DOMException && error.name === 'NotFoundError') {
        return;
      } else {
        toast.error('Failed to request Bluetooth permissions');
      }
    }
  };

  const handleScan = async () => {
    setIsScanning(true);
    setDevices([]);
    try {
      if (!isBluetoothAvailable) {
        toast.error('Bluetooth is not available on this device or browser.');
        setIsScanning(false);
        return;
      }
      if (!hasPermissions) {
        await requestPermissions();
        setIsScanning(false);
        return;
      }
      const foundDevices = await bluetoothScan();

      console.log('Found devices:', foundDevices);
      // Convert the device format to include the connected property
      const formattedDevices: BluetoothDevice[] = foundDevices.map(device => ({
        ...device,
        connected: false,
        device: device.device || {}
      }));
      
      setDevices(formattedDevices);

      if (formattedDevices.length === 0) {
        toast.info('No transfer-enabled devices found. Try scanning again.');
      }
    } catch (error) {
      console.error('Bluetooth scan error:', error);
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        toast.info('Bluetooth scan was cancelled.');
      } else if (error instanceof DOMException && error.name === 'SecurityError') {
        toast.error('Bluetooth access was blocked. Please enable Bluetooth permissions.');
        setHasPermissions(false);
      } else {
        toast.error('Failed to scan for devices. Please ensure Bluetooth is enabled.');
      }
    } finally {
      setIsScanning(false);
    }
  };

  const handleConnect = async (device: BluetoothDevice) => {
    try {
      toast.info(`Connecting to ${device.name || 'device'}...`);

      const connected = await connectToDevice(device.id, device.device).catch((error) => {
        if (error.message === 'Device not verified') {
          return new Promise((resolve) => {
            toast.error(
              <div className="flex flex-col gap-2">
                <p>Device is not a verified client.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigate('/payment');
                    resolve(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Mail size={16} />
                  Switch to Email Transfer
                </Button>
              </div>,
              { duration: 5000 }
            );
            resolve(false);
          });
        }
        throw error;
      });

      if (connected) {
        setSelectedDevice(device);
        setConnectedDevice(device);
        toast.success(`Connected to ${device.name}`);
      }
    } catch (error) {
      console.error('Connection error:', error);
      toast.error('Failed to connect to device.');
    }
  };

  return (
    <div className="w-full space-y-6 animate-fade-in">
      {!isBluetoothAvailable && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Bluetooth Not Available</h3>
              <p className="text-xs text-yellow-700 mt-1">
                Web Bluetooth is not supported in this browser or device. Please try using Chrome, Edge, or Opera on desktop, or Chrome on Android.
              </p>
            </div>
          </div>
        </div>
      )}

      <BluetoothScanButton
        isScanning={isScanning}
        isBluetoothAvailable={isBluetoothAvailable}
        onClick={handleScan}
      />

      <div className="space-y-4 mt-6">
        {devices.length > 0 ? (
          <BluetoothDeviceList
            devices={devices}
            selectedDevice={selectedDevice}
            handleConnect={handleConnect}
          />
        ) : (
          <BluetoothNoDevices isScanning={isScanning} />
        )}
      </div>
    </div>
  );
};

export default BluetoothScanner;
